import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Plus, Trash2, Check, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [occupants, setOccupants] = useState([{ name: '', age: '' }]);
  const [creditCard, setCreditCard] = useState({
    card_type: 'visa',
    card_number: '',
    expiry_date: '',
    cvv: '',
    cardholder_name: '',
    cardholder_address: '',
    cardholder_city: '',
    cardholder_province: '',
    cardholder_postal: '',
    cardholder_country: 'Canada',
    cardholder_phone: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  
  // Signature canvas state
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    // Initialize canvas
    if (canvasRef.current && contract?.status === 'pending') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [contract]);

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API}/public/contract/${token}`);
      setContract(response.data);
      
      // Pre-fill first occupant with renter name
      if (response.data.renter_name) {
        setOccupants([{ name: response.data.renter_name, age: '' }]);
      }
      
      // Pre-fill cardholder with renter info
      setCreditCard(prev => ({
        ...prev,
        cardholder_name: response.data.renter_name || '',
        cardholder_address: response.data.renter_address || '',
        cardholder_city: response.data.renter_city || '',
        cardholder_province: response.data.renter_province || '',
        cardholder_postal: response.data.renter_postal || '',
        cardholder_phone: response.data.renter_phone || ''
      }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Contract not found');
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (canvasRef.current && isDrawing) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.closePath();
      setSignatureData(canvas.toDataURL('image/png'));
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignatureData(null);
  };

  // Occupant handlers
  const addOccupant = () => {
    if (occupants.length < 14) {
      setOccupants([...occupants, { name: '', age: '' }]);
    }
  };

  const removeOccupant = (index) => {
    if (occupants.length > 1) {
      setOccupants(occupants.filter((_, i) => i !== index));
    }
  };

  const updateOccupant = (index, field, value) => {
    const updated = [...occupants];
    updated[index][field] = value;
    setOccupants(updated);
  };

  // Credit card handlers
  const updateCreditCard = (field, value) => {
    setCreditCard(prev => ({ ...prev, [field]: value }));
  };

  const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.slice(0, 16);
  };

  const formatExpiryDate = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 2) {
      return digits.slice(0, 2) + '/' + digits.slice(2, 4);
    }
    return digits;
  };

  // Submit handler
  const handleSubmit = async () => {
    // Validation
    if (!occupants.some(o => o.name.trim())) {
      toast.error('Please add at least one occupant');
      return;
    }
    
    if (!creditCard.card_number || creditCard.card_number.length < 13) {
      toast.error('Please enter a valid card number');
      return;
    }
    
    if (!creditCard.expiry_date || creditCard.expiry_date.length < 5) {
      toast.error('Please enter a valid expiry date');
      return;
    }
    
    if (!creditCard.cvv || creditCard.cvv.length < 3) {
      toast.error('Please enter a valid CVV');
      return;
    }
    
    if (!creditCard.cardholder_name) {
      toast.error('Please enter the cardholder name');
      return;
    }
    
    if (!agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    
    if (!hasSignature) {
      toast.error('Please sign the contract');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const filteredOccupants = occupants.filter(o => o.name.trim());
      
      await axios.post(`${API}/public/contract/${token}/sign`, {
        occupants: filteredOccupants,
        credit_card: creditCard,
        signature_data: signatureData,
        use_stripe: false
      });
      
      toast.success('Contract signed successfully!');
      
      // Refresh to show signed status
      await fetchContract();
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign contract');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Contract Not Found</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (contract?.status === 'signed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Check className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Contract Signed!</h2>
            <p className="text-gray-600 mb-4">
              Thank you for signing your rental contract. A copy has been sent to your email.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 text-left text-sm">
              <p className="font-medium mb-2">Rental Summary:</p>
              <p>Check-in: {contract.check_in_date} at {contract.check_in_time}</p>
              <p>Check-out: {contract.check_out_date} at {contract.check_out_time}</p>
              <p className="mt-2 font-medium">Total: ${contract.total_amount?.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (contract?.status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Contract Expired</h2>
            <p className="text-gray-600">
              This contract link has expired. Please contact us at neill@neilldixon.com for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <Toaster position="top-right" />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Big Bear Chalet</h1>
          <p className="text-gray-600">Rental Contract</p>
        </div>

        {/* Pre-filled Information (Read-only) */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Rental Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Renter Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
                  <p><span className="text-gray-500">Name:</span> {contract.renter_name}</p>
                  <p><span className="text-gray-500">Email:</span> {contract.renter_email}</p>
                  <p><span className="text-gray-500">Phone:</span> {contract.renter_phone}</p>
                  <p><span className="text-gray-500">Address:</span> {contract.renter_address}</p>
                  <p><span className="text-gray-500">City:</span> {contract.renter_city}, {contract.renter_province} {contract.renter_postal}</p>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Rental Period</h3>
                <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
                  <p><span className="text-gray-500">Property:</span> {contract.property_name}</p>
                  <p><span className="text-gray-500">Check-in:</span> {contract.check_in_date} at {contract.check_in_time}</p>
                  <p><span className="text-gray-500">Check-out:</span> {contract.check_out_date} at {contract.check_out_time}</p>
                  <p><span className="text-gray-500">Nights:</span> {contract.num_nights}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="font-medium text-gray-700 mb-2">Financial Summary</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-gray-500">Rental Amount:</p>
                  <p className="text-right">${contract.rental_amount?.toLocaleString()}</p>
                  <p className="text-gray-500">HST (13%):</p>
                  <p className="text-right">${contract.hst_amount?.toLocaleString()}</p>
                  <p className="font-medium border-t pt-2">Total:</p>
                  <p className="text-right font-medium border-t pt-2">${contract.total_amount?.toLocaleString()}</p>
                </div>
                
                {(contract.payment_1_amount || contract.payment_2_amount || contract.payment_3_amount) && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="font-medium mb-2">Payment Schedule:</p>
                    {contract.payment_1_amount > 0 && (
                      <p className="text-sm">Payment 1: ${contract.payment_1_amount?.toLocaleString()} due {contract.payment_1_date}</p>
                    )}
                    {contract.payment_2_amount > 0 && (
                      <p className="text-sm">Payment 2: ${contract.payment_2_amount?.toLocaleString()} due {contract.payment_2_date}</p>
                    )}
                    {contract.payment_3_amount > 0 && (
                      <p className="text-sm">Payment 3: ${contract.payment_3_amount?.toLocaleString()} due {contract.payment_3_date}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Occupants Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Occupants (Max 14)</span>
              {occupants.length < 14 && (
                <Button variant="outline" size="sm" onClick={addOccupant}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-4">Please list all persons who will be staying at the property.</p>
            <div className="space-y-3">
              {occupants.map((occupant, index) => (
                <div key={index} className="flex gap-3 items-center">
                  <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                  <Input
                    placeholder="Full Name"
                    value={occupant.name}
                    onChange={(e) => updateOccupant(index, 'name', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Age"
                    value={occupant.age}
                    onChange={(e) => updateOccupant(index, 'age', e.target.value)}
                    className="w-20"
                  />
                  {occupants.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeOccupant(index)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Credit Card Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Credit Card Authorization</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-4">
              Your credit card will be held for a security deposit of ${contract.security_deposit?.toLocaleString()}. 
              This will only be charged in the event of damages.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Card Type</Label>
                <Select value={creditCard.card_type} onValueChange={(v) => updateCreditCard('card_type', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visa">Visa</SelectItem>
                    <SelectItem value="mastercard">Mastercard</SelectItem>
                    <SelectItem value="amex">American Express</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Card Number</Label>
                <Input
                  placeholder="1234 5678 9012 3456"
                  value={creditCard.card_number}
                  onChange={(e) => updateCreditCard('card_number', formatCardNumber(e.target.value))}
                  maxLength={16}
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  placeholder="MM/YY"
                  value={creditCard.expiry_date}
                  onChange={(e) => updateCreditCard('expiry_date', formatExpiryDate(e.target.value))}
                  maxLength={5}
                />
              </div>
              <div>
                <Label>CVV</Label>
                <Input
                  placeholder="123"
                  value={creditCard.cvv}
                  onChange={(e) => updateCreditCard('cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  maxLength={4}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Cardholder Name</Label>
                <Input
                  placeholder="Name as it appears on card"
                  value={creditCard.cardholder_name}
                  onChange={(e) => updateCreditCard('cardholder_name', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Billing Address</Label>
                <Input
                  placeholder="Street Address"
                  value={creditCard.cardholder_address}
                  onChange={(e) => updateCreditCard('cardholder_address', e.target.value)}
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={creditCard.cardholder_city}
                  onChange={(e) => updateCreditCard('cardholder_city', e.target.value)}
                />
              </div>
              <div>
                <Label>Province</Label>
                <Input
                  value={creditCard.cardholder_province}
                  onChange={(e) => updateCreditCard('cardholder_province', e.target.value)}
                />
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input
                  value={creditCard.cardholder_postal}
                  onChange={(e) => updateCreditCard('cardholder_postal', e.target.value)}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={creditCard.cardholder_phone}
                  onChange={(e) => updateCreditCard('cardholder_phone', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Terms & Conditions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Terms & Conditions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm max-h-96 overflow-y-auto prose prose-sm max-w-none">
              {contract.terms_and_conditions ? (
                <div dangerouslySetInnerHTML={{ __html: contract.terms_and_conditions }} />
              ) : (
                <ul className="space-y-2">
                  <li>• Maximum occupancy is 14 persons. Events and parties are not permitted.</li>
                  <li>• Check-in time is 4:30 PM and check-out is 11:00 AM.</li>
                  <li>• No smoking inside the property.</li>
                  <li>• Pets are not allowed unless pre-approved.</li>
                  <li>• Quiet hours are from 11:00 PM to 8:00 AM.</li>
                  <li>• The renter is responsible for any damages beyond normal wear and tear.</li>
                  <li>• Security deposit of ${contract.security_deposit?.toLocaleString()} will be refunded within 14 days after checkout if no damages.</li>
                  <li>• Cancellation policy: Full refund if cancelled 60+ days before check-in. 50% refund 30-60 days. No refund within 30 days.</li>
                </ul>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
              />
              <Label htmlFor="terms" className="text-sm cursor-pointer">
                I have read and agree to the Terms and Conditions
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Signature</span>
              {hasSignature && (
                <Button variant="outline" size="sm" onClick={clearSignature}>
                  Clear
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-4">Please sign below using your mouse or finger.</p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
              <canvas
                ref={canvasRef}
                width={600}
                height={150}
                className="w-full bg-white cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            {!hasSignature && (
              <p className="text-sm text-gray-400 mt-2 text-center">Draw your signature above</p>
            )}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={submitting || !agreedToTerms || !hasSignature}
            className="px-12"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Signing...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Sign Contract
              </>
            )}
          </Button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Questions? Contact us at neill@neilldixon.com</p>
        </div>
      </div>
    </div>
  );
}
